import { useState } from 'react'
import { api, apiForm } from '../api'
import { Drawer, Upload, Button, Card, Checkbox, Select, Input, Typography, Space, Tag, Result, message, Spin, Alert } from 'antd'
import { InboxOutlined, WarningOutlined, CheckCircleOutlined, IdcardOutlined, UserOutlined } from '@ant-design/icons'
import useIsMobile from '../hooks/useIsMobile'

const { Text } = Typography

// Subir el PDF de nóminas de la asesoría, revisar a quién va cada página y guardar.
// Hasta pulsar "Guardar" no se guarda nada ni se avisa a nadie.
export default function FragmentadorNominas({ abierto, onCerrar, trabajadores }) {
  const isMobile = useIsMobile()
  const [fase, setFase] = useState('subir') // subir | revisar | hecho
  const [analizando, setAnalizando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [lote, setLote] = useState(null)
  const [filas, setFilas] = useState([])
  const [avisar, setAvisar] = useState(true)
  const [pedirFirma, setPedirFirma] = useState(true)
  const [resultado, setResultado] = useState(null)

  const reiniciar = () => {
    setFase('subir'); setLote(null); setFilas([]); setResultado(null); setAvisar(true); setPedirFirma(true)
  }

  const cerrar = async (huboCambios) => {
    if (fase === 'revisar' && lote) api(`/nominas/lote/${lote.lote_id}`, { method: 'DELETE' }).catch(() => {})
    reiniciar()
    onCerrar(huboCambios)
  }

  const analizar = async (archivo) => {
    setAnalizando(true)
    try {
      const fd = new FormData()
      fd.append('archivo', archivo)
      const r = await apiForm('/nominas/analizar', fd)
      // Marcadas por defecto: las reconocidas que no están ya guardadas y, si una
      // nómina sale repetida en el mismo PDF, solo la primera.
      const vistas = new Set()
      setFilas(r.paginas.map(p => {
        const clave = `${p.trabajador_id}|${p.nombre}`
        const primera = !vistas.has(clave)
        vistas.add(clave)
        return { ...p, marcada: p.estado === 'ok' && !p.ya_existe && primera, sustituir: false }
      }))
      setLote(r)
      setFase('revisar')
    } catch (err) {
      message.error(err.message)
    }
    setAnalizando(false)
    return false
  }

  const cambiar = (pagina, cambios) => setFilas(fs => fs.map(f => f.pagina === pagina ? { ...f, ...cambios } : f))

  const marcadas = filas.filter(f => f.marcada)

  const guardar = async () => {
    if (marcadas.some(f => !f.trabajador_id)) { message.error('Hay páginas marcadas sin trabajador: elige a quién van o desmárcalas'); return }
    setGuardando(true)
    try {
      const r = await api('/nominas/confirmar', {
        method: 'POST',
        body: JSON.stringify({
          lote_id: lote.lote_id,
          avisar,
          requiere_firma: pedirFirma,
          paginas: marcadas.map(f => ({ pagina: f.pagina, trabajador_id: f.trabajador_id, nombre: f.nombre, sustituir: f.ya_existe && !f.ya_firmada && f.sustituir })),
        }),
      })
      setResultado(r)
      setFase('hecho')
    } catch (err) {
      message.error(err.message)
    }
    setGuardando(false)
  }

  const opcionesTrab = trabajadores.map(t => ({ value: t.id, label: `${t.nombre} ${t.apellidos || ''}`.trim() + (t.activo ? '' : ' (de baja)') }))

  return (
    <Drawer
      title="Subir nóminas"
      open={abierto}
      onClose={() => cerrar(fase === 'hecho')}
      width={isMobile ? '100%' : 620}
      footer={fase === 'revisar' && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Checkbox checked={pedirFirma} onChange={e => setPedirFirma(e.target.checked)}>Pedir que las firmen (no podrán descargarlas hasta firmar)</Checkbox>
          <Checkbox checked={avisar} onChange={e => setAvisar(e.target.checked)}>Avisar a cada trabajador por correo</Checkbox>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => cerrar(false)}>Descartar</Button>
            <Button type="primary" loading={guardando} disabled={marcadas.length === 0} onClick={guardar} style={{ background: '#2E7D32' }}>
              Guardar {marcadas.length} {marcadas.length === 1 ? 'nómina' : 'nóminas'}
            </Button>
          </Space>
        </Space>
      )}
    >
      {fase === 'subir' && (
        <Spin spinning={analizando} description="Leyendo el PDF...">
          <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
            Sube el PDF que te manda la asesoría con las nóminas del mes. La aplicación lo corta por páginas y reconoce de quién es cada una por su DNI/NIE.
            Antes de guardar podrás revisarlo todo.
          </Text>
          <Upload.Dragger accept=".pdf" showUploadList={false} beforeUpload={analizar} disabled={analizando}>
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Pulsa o arrastra aquí el PDF de nóminas</p>
          </Upload.Dragger>
        </Spin>
      )}

      {fase === 'revisar' && (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text type="secondary">
            {lote.total} {lote.total === 1 ? 'página' : 'páginas'}. Revisa a quién va cada una; solo se guardan las marcadas.
          </Text>
          {filas.map(f => (
            <Card key={f.pagina} size="small" style={{
              borderRadius: '10px',
              borderLeft: `3px solid ${f.estado !== 'ok' ? '#ff4d4f' : f.ya_existe || f.repetida_en_lote ? '#faad14' : '#52c41a'}`,
              opacity: f.marcada ? 1 : 0.75,
            }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <Checkbox checked={f.marcada} onChange={e => cambiar(f.pagina, { marcada: e.target.checked })} style={{ marginTop: '4px' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px', marginBottom: '6px' }}>
                    <Text strong style={{ fontSize: '12px' }}>Página {f.pagina}</Text>
                    {f.empresa && <Text type="secondary" style={{ fontSize: '11px' }}>{f.empresa}</Text>}
                  </div>
                  <Select
                    size="small"
                    placeholder="¿De quién es?"
                    value={f.trabajador_id}
                    onChange={v => cambiar(f.pagina, { trabajador_id: v, marcada: true })}
                    options={opcionesTrab}
                    style={{ width: '100%', marginBottom: '6px' }}
                  />
                  <Input size="small" value={f.nombre} onChange={e => cambiar(f.pagina, { nombre: e.target.value })} />
                  <div style={{ marginTop: '6px', fontSize: '11px' }}>
                    {f.estado === 'ok' && f.reconocido_por === 'documento' && <Text style={{ fontSize: '11px', color: '#2E7D32' }}><IdcardOutlined /> Reconocido por su DNI/NIE</Text>}
                    {f.estado === 'ok' && f.reconocido_por === 'nombre' && <Text style={{ fontSize: '11px', color: '#2E7D32' }}><UserOutlined /> Reconocido por el nombre</Text>}
                    {f.estado !== 'ok' && <Text type="danger" style={{ fontSize: '11px' }}><WarningOutlined /> {f.motivo}</Text>}
                    {f.de_baja && <Tag color="error" style={{ marginLeft: '6px', fontSize: '10px' }}>De baja</Tag>}
                    {f.repetida_en_lote && <div><Text style={{ fontSize: '11px', color: '#d48806' }}><WarningOutlined /> Esta nómina sale más de una vez en el PDF</Text></div>}
                    {f.ya_existe && (
                      <div style={{ marginTop: '4px' }}>
                        <Text style={{ fontSize: '11px', color: '#d48806' }}><WarningOutlined /> Ya tiene una «{f.nombre}» guardada.</Text>
                        {f.ya_firmada && (
                          <div><Text type="secondary" style={{ fontSize: '11px' }}>Esa ya está firmada por el trabajador, así que no se puede sustituir: si la guardas, se añade como otra copia.</Text></div>
                        )}
                        {f.marcada && !f.ya_firmada && (
                          <div>
                            <Checkbox checked={f.sustituir} onChange={e => cambiar(f.pagina, { sustituir: e.target.checked })} style={{ fontSize: '11px' }}>
                              Sustituir la anterior (si no, se guarda como otra copia)
                            </Checkbox>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </Space>
      )}

      {fase === 'hecho' && resultado && (
        <Result
          status={resultado.errores.length ? 'warning' : 'success'}
          title={`${resultado.guardadas.length} ${resultado.guardadas.length === 1 ? 'nómina guardada' : 'nóminas guardadas'}`}
          subTitle={resultado.omitidas > 0 ? `${resultado.omitidas} ${resultado.omitidas === 1 ? 'página no se ha guardado' : 'páginas no se han guardado'} (sin marcar)` : null}
          extra={[
            <Button key="otro" onClick={reiniciar}>Subir otro PDF</Button>,
            <Button key="cerrar" type="primary" onClick={() => cerrar(true)}>Cerrar</Button>,
          ]}
        >
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {resultado.guardadas.map(g => (
              <Text key={g.pagina} style={{ fontSize: '12px' }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} /> {g.trabajador}: {g.nombre}{g.sustituida ? ' (sustituye a la anterior)' : ''}
                {g.aviso === 'enviado' && <Text type="secondary" style={{ fontSize: '11px' }}> · avisado por correo</Text>}
                {g.aviso === 'sin_email' && <Text type="secondary" style={{ fontSize: '11px' }}> · sin correo en su ficha, no avisado</Text>}
                {g.aviso === 'error' && <Text type="danger" style={{ fontSize: '11px' }}> · el correo de aviso ha fallado</Text>}
              </Text>
            ))}
            {resultado.errores.map(e => (
              <Alert key={e.pagina} type="error" showIcon title={`Página ${e.pagina}: ${e.error}`} />
            ))}
          </Space>
        </Result>
      )}
    </Drawer>
  )
}
